import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import PlaceWidget from '../PlaceWidget';
import usePopup from '../../common/popup/usePopup';
import Popup from '../../common/popup/Popup';
import ColumnTitle from './ColumnTitle';
import '../../../styles/components/itinerary/Column.css';
import { usePlaceContext } from '../PlaceContext';
import useApi from '../../../Api';
import { debounce } from 'lodash';
import { useAuth } from '../../../AuthContext';

const Column = ({
  column,
  index,
  handleDrop,
  handleDragOver,
  handleDragLeave,
  handleColumnTitleChange,
  handleColumnBlur,
  handleColumnKeyDown,
  startColumnEditing,
  setDragData,
  handleRemoveWidget,
  deleteColumn,
  handleDateChange,
  handleNotesChange,
  readOnly
}) => {
  const { isPopupVisible, showPopup, hidePopup } = usePopup();
  const { state, stateRef, setColumnVisibility, setColumnMarkerColor } = usePlaceContext();
  const [currentColumnToDelete, setCurrentColumnToDelete] = useState(null);
  const [isVisible, setIsVisible] = useState(true);
  const [colorPickerVisible, setColorPickerVisible] = useState(false);
  const [color, setColor] = useState(state.selectedItinerary.columnMarkerColors[column.id] || '#FFFFFF');
  const colorPickerRef = useRef(null);
  const apiClient = useApi();
  const { token } = useAuth();

  useEffect(() => {
    const visibility = state.selectedItinerary.columnVisibility[column.id];
    setIsVisible(visibility !== undefined ? visibility : true);
  }, [state.selectedItinerary.columnVisibility, column.id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target)) {
        setColorPickerVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [colorPickerRef]);

  const handleDeleteClick = (columnId) => {
    if (readOnly) return;
    setCurrentColumnToDelete(columnId);
    showPopup();
  };

  const handleConfirmDelete = () => {
    deleteColumn(currentColumnToDelete);
    hidePopup();
  };

  const toggleMarkers = useCallback(debounce(async () => {
    const newVisibility = !isVisible;
    setIsVisible(newVisibility);
    setColumnVisibility(column.id, newVisibility);
    if (!readOnly) {
      const itineraryId = state.selectedItinerary.id;
      try {
        const endpoint = newVisibility ? `/columns/${column.id}/show` : `/columns/${column.id}/hide`;
        await apiClient.patch(`${endpoint}?itinerary_id=${itineraryId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Error updating column visibility:', error);
      }
    }
  }, 200), [isVisible, column.id, setColumnVisibility, state, apiClient, token, readOnly]);

  const toggleColorPicker = () => {
    setColorPickerVisible(!colorPickerVisible);
  };

  const handleChangeComplete = useCallback(debounce(async (color) => {
    setColor(color.hex);
    setColumnMarkerColor(column.id, color.hex);
    if (!readOnly) {
      const itineraryId = state.selectedItinerary.id;
      try {
        await apiClient.patch(`/columns/${column.id}/color?itinerary_id=${itineraryId}`, { color: color.hex }, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (error) {
        console.error('Error updating column color:', error);
      }
    }
  }, 200), [column.id, state, apiClient, setColumnMarkerColor, token, readOnly]);

  return (
    <div key={column.id} id={column.id} className="column" onDrop={(e) => handleDrop(e, column.id)} onDragOver={(e) => handleDragOver(e, column.id)} onDragLeave={(e) => handleDragLeave(e, column.id)} role="list" aria-labelledby={`column-title-${index}`}>
      <ColumnTitle
        title={column.title}
        index={index}
        isEditing={column.isEditing}
        startEditing={() => !readOnly && startColumnEditing(index)}
        handleTitleChange={(e) => handleColumnTitleChange(index, e.target.value, column.id)}
        handleBlur={() => handleColumnBlur(index)}
        handleKeyDown={(e) => handleColumnKeyDown(e, index)}
        handleDelete={() => handleDeleteClick(column.id)}
        handleDateChange={handleDateChange}
        date={column.date}
        toggleMarkers={toggleMarkers}
        toggleColorPicker={toggleColorPicker}
        isVisible={isVisible}
        colorPickerVisible={colorPickerVisible}
        handleChangeComplete={handleChangeComplete}
        color={color}
        colorPickerRef={colorPickerRef}
        readOnly={readOnly}
      />
      <div className="column-items-box">
        {column.items.map((item, idx) => (
          <div key={item.id} data-index={idx} draggable={!readOnly} role="listitem" aria-dropeffect={readOnly ? "none" : "move"}>
            <PlaceWidget
              place={item}
              columnId={column.id}
              placeIndex={idx}
              onRemove={() => handleRemoveWidget(column.id, item, false, false)}
              setDragData={setDragData}
              onNotesChange={(newNotes) => handleNotesChange(column.id, item.id, newNotes)}
              readOnly={readOnly}
            />
          </div>
        ))}
      </div>
      {!readOnly && isPopupVisible && (
        <Popup title="Confirm Delete" message="Confirm you would like to delete this column?" onConfirm={handleConfirmDelete} onCancel={hidePopup} />
      )}
    </div>
  );
};

Column.propTypes = {
  column: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  handleDrop: PropTypes.func.isRequired,
  handleDragOver: PropTypes.func.isRequired,
  handleDragLeave: PropTypes.func.isRequired,
  handleColumnTitleChange: PropTypes.func.isRequired,
  handleColumnBlur: PropTypes.func.isRequired,
  handleColumnKeyDown: PropTypes.func.isRequired,
  startColumnEditing: PropTypes.func.isRequired,
  setDragData: PropTypes.func.isRequired,
  handleRemoveWidget: PropTypes.func.isRequired,
  deleteColumn: PropTypes.func.isRequired,
  handleDateChange: PropTypes.func.isRequired,
  handleNotesChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

export default Column;
